import { Flex, Group, IconFont, Popover, Slider, Text } from '@byecode/ui'
import { CALENDAR_MODE, FilterMode } from '@lighthouse/core'
import {
    FilledInput,
    FillPickerPopoverDropdown,
    getAppointField,
    getFieldOptions,
    getIsFindUseInFilter,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    PopoverDropdownWithClose,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { FieldSelect } from '@/components/FieldSelect'
import { InheritLabel } from '@/components/InheritLabel'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { BorderField } from '@/components/StyleSetting/components/BorderField'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { FontFamilyControl } from '../../FontFamilyControl'
import { ListItemMenu } from '../../ListItemMenu'
import type { BaseFieldInputSetting } from '../types'

interface CalendarConfigProps extends BaseFieldInputSetting {}

const CollapseButton = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    appearance: none;
    display: flex;
    align-items: center;
    background-color: rgba(38, 65, 90, 0.06);
    padding: 6px;
    border-radius: 50%;
    &:hover {
        background-color: var(--color-gray-200);
    }
`

export const CalendarHeader: React.FunctionComponent<CalendarConfigProps> = props => {
    const { control, watch } = useFormContext /* <FieldBlockConfig> */()

    const colorSystemMethods = useColorSystemAction()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const mode = watch('calendar.mode')
    const isWeekMode = mode === CALENDAR_MODE.week

    if (isWeekMode) {
        return null
    }

    return (
        <>
            <Popover
                autoUpdate
                withinPortal
                position="left-start"
                offsetOptions={{
                    // crossAxis: 100,
                    mainAxis: 24
                }}
            >
                <Popover.Target>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="日历头部" name="breakPoint.calendarHeader" />
                        <IconFont type="ArrowRightSmall" size={16} color="var(--color-gray-400)" />
                    </ListItem4ByecodeUi>
                </Popover.Target>
                <PopoverDropdownWithClose title="日历头部">
                    <Controller
                        control={control}
                        name="breakPoint.calendarHeader.fontFamily"
                        render={({ field }) => (
                            <FontFamilyControl
                                value={field.value}
                                name="breakPoint.calendarHeader.fontFamily"
                                // fontFamilyInputProps={fontFamilyInputProps}
                                onChange={v => {
                                    field.onChange(v)
                                    handleSetBreakKey('breakPoint.calendarHeader.fontFamily')
                                }}
                            />
                        )}
                    />
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="文本颜色" name="breakPoint.calendarHeader.fontColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarHeader.fontColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarHeader.fontColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="文本颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarHeader.fontColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="图标颜色" name="breakPoint.calendarHeader.iconColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarHeader.iconColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarHeader.iconColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="图标颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarHeader.iconColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </PopoverDropdownWithClose>
            </Popover>
        </>
    )
}

export const WeekNumber: React.FC = () => {
    const { control } = useFormContext /* <FieldBlockConfig> */()

    const colorSystemMethods = useColorSystemAction()
    const { handleSetBreakKey } = useSetExtendsKeys()
    return (
        <Popover
            autoUpdate
            withinPortal
            position="left-start"
            offsetOptions={{
                // crossAxis: 100,
                mainAxis: 24
            }}
        >
            <Popover.Target>
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="星期数" name="breakPoint.calendarWeek" />
                    <IconFont type="ArrowRightSmall" size={16} color="var(--color-gray-400)" />
                </ListItem4ByecodeUi>
            </Popover.Target>
            <PopoverDropdownWithClose title="星期数">
                <Controller
                    control={control}
                    name="breakPoint.calendarWeek.fontFamily"
                    render={({ field }) => (
                        <FontFamilyControl
                            value={field.value}
                            name="breakPoint.calendarWeek.fontFamily"
                            onChange={v => {
                                field.onChange(v)
                                handleSetBreakKey('breakPoint.calendarWeek.fontFamily')
                            }}
                        />
                    )}
                />
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="文本颜色" name="breakPoint.calendarWeek.fontColor" />
                    <Controller
                        control={control}
                        name="breakPoint.calendarWeek.fontColor"
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarWeek.fontColor')
                                        }}
                                        style={{ width: 180 }}
                                    />
                                </Popover.Target>
                                <FillPickerPopoverDropdown
                                    title="文本颜色"
                                    value={field.value}
                                    onChange={v => {
                                        field.onChange(v)
                                        handleSetBreakKey('breakPoint.calendarWeek.fontColor')
                                    }}
                                    {...colorSystemMethods}
                                />
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>
            </PopoverDropdownWithClose>
        </Popover>
    )
}

export const DateNumber: React.FC = () => {
    const { control, watch } = useFormContext /* <FieldBlockConfig> */()
    const colorSystemMethods = useColorSystemAction()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const mode = watch('calendar.mode')
    const isWeekMode = mode === CALENDAR_MODE.week

    return (
        <Popover
            autoUpdate
            withinPortal
            position="left-start"
            offsetOptions={{
                // crossAxis: 100,
                mainAxis: 24
            }}
        >
            <Popover.Target>
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="日期数" name="breakPoint.calendarDate.fontFamily" />
                    <IconFont type="ArrowRightSmall" size={16} color="var(--color-gray-400)" />
                </ListItem4ByecodeUi>
            </Popover.Target>
            <PopoverDropdownWithClose title="日期数">
                <Controller
                    control={control}
                    name="breakPoint.calendarDate.fontFamily"
                    render={({ field }) => (
                        <FontFamilyControl
                            value={field.value}
                            name="breakPoint.calendarDate.fontFamily"
                            // fontFamilyInputProps={fontFamilyInputProps}
                            onChange={v => {
                                field.onChange(v)
                                handleSetBreakKey('breakPoint.calendarDate.fontFamily')
                            }}
                        />
                    )}
                />
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="日期颜色" name="breakPoint.calendarDate.currentMonthFontColor" />
                    <Controller
                        control={control}
                        name="breakPoint.calendarDate.currentMonthFontColor"
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.currentMonthFontColor')
                                        }}
                                        style={{ width: 180 }}
                                    />
                                </Popover.Target>
                                <FillPickerPopoverDropdown
                                    title="本月日期颜色"
                                    value={field.value}
                                    onChange={v => {
                                        field.onChange(v)
                                        handleSetBreakKey('breakPoint.calendarDate.currentMonthFontColor')
                                    }}
                                    {...colorSystemMethods}
                                />
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>
                {isWeekMode ? null : (
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="非本月日期颜色" name="breakPoint.calendarDate.notCurrentMonthFontColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarDate.notCurrentMonthFontColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarDate.notCurrentMonthFontColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="非本月日期颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.notCurrentMonthFontColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                )}
                <Group
                    label="更多配置"
                    renderRightIcon={(open, onChange) => {
                        return (
                            <CollapseButton onClick={() => onChange(!open)}>
                                <IconFont
                                    type="ArrowDownSmall"
                                    size={16}
                                    color="var(--color-gray-400)"
                                    style={{ transform: open ? 'rotate(-180deg)' : undefined, transition: 'transform 0.2s' }}
                                />
                            </CollapseButton>
                        )
                    }}
                    styles={{
                        root: { margin: '0 -8px', padding: 0 },
                        label: { marginLeft: 'auto', fontWeight: 400 },
                        rightIcon: { marginLeft: 'unset' }
                    }}
                >
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="选中背景颜色" name="breakPoint.calendarDate.selectedBackgroundColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarDate.selectedBackgroundColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarDate.selectedBackgroundColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="选中背景颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.selectedBackgroundColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="选中文本颜色" name="breakPoint.calendarDate.selectedFontColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarDate.selectedFontColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarDate.selectedBackgroundColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="选中文本颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.selectedBackgroundColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="悬停背景颜色" name="breakPoint.calendarDate.hoverBackgroundColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarDate.hoverBackgroundColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarDate.hoverBackgroundColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="悬停背景颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.hoverBackgroundColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>{' '}
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <InheritLabel label="悬停文本颜色" name="breakPoint.calendarDate.hoverFontColor" />
                        <Controller
                            control={control}
                            name="breakPoint.calendarDate.hoverFontColor"
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={v => {
                                                field.onChange(v)
                                                handleSetBreakKey('breakPoint.calendarDate.hoverFontColor')
                                            }}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="悬停文本颜色"
                                        value={field.value}
                                        onChange={v => {
                                            field.onChange(v)
                                            handleSetBreakKey('breakPoint.calendarDate.hoverFontColor')
                                        }}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </Group>
            </PopoverDropdownWithClose>
        </Popover>
    )
}

export const CalendarConfig: React.FunctionComponent<CalendarConfigProps> = props => {
    const { control, watch } = useFormContext /* <FieldBlockConfig> */()
    const colorSystemMethods = useColorSystemAction()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const mode = watch('calendar.mode')
    const isWeekMode = mode === CALENDAR_MODE.week

    return (
        <Group label="日历样式">
            <CalendarHeader {...props} />
            <WeekNumber />
            <DateNumber />
            {isWeekMode && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="日期展示数量" name="breakPoint.calendar.weekShowCount" />
                    <Controller
                        control={control}
                        name="breakPoint.calendar.weekShowCount"
                        render={({ field }) => (
                            <Slider
                                min={3}
                                max={7}
                                defaultValue={7}
                                style={{ width: 180 }}
                                value={field.value}
                                onChange={v => {
                                    field.onChange(v)
                                    handleSetBreakKey('breakPoint.calendar.weekShowCount')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="背景" name="breakPoint.calendar.backgroundColor" />
                <Controller
                    control={control}
                    name="breakPoint.calendar.backgroundColor"
                    render={({ field }) => (
                        <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                            <Popover.Target>
                                <FilledInput
                                    value={field.value}
                                    onChange={v => {
                                        field.onChange(v)
                                        handleSetBreakKey('breakPoint.calendar.backgroundColor')
                                    }}
                                    style={{ width: 180 }}
                                />
                            </Popover.Target>
                            <FillPickerPopoverDropdown
                                title="背景"
                                value={field.value}
                                onChange={v => {
                                    field.onChange(v)
                                    handleSetBreakKey('breakPoint.calendar.backgroundColor')
                                }}
                                {...colorSystemMethods}
                            />
                        </Popover>
                    )}
                />
            </ListItem4ByecodeUi>
            <Controller
                control={control}
                name="breakPoint.calendar.border"
                render={({ field }) => (
                    <BorderField
                        label={<InheritLabel label="描边" name="breakPoint.calendar.border" />}
                        value={field.value}
                        onChange={v => {
                            if (isDeepEqual(v, field.value)) {
                                return
                            }
                            field.onChange(v)
                            handleSetBreakKey('breakPoint.calendar.border')
                        }}
                    />
                )}
            />
        </Group>
    )
}

export const CalendarDataConfig: React.FunctionComponent<CalendarConfigProps> = props => {
    const { control, watch, setValue } = useFormContext()
    const colorSystemMethods = useColorSystemAction()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const [dsId, calendarEnable] = watch(['calendar.describeDsId', 'calendar.enabled'])
    const dataSource = useDataSource(appId, envId, dsId)
    const { schema = {}, viewOptions: { tableProps = [] } = {} } = dataSource || {}
    const columns = getViewColumns({
        tableProps,
        schema
    })

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const findUseObject = useFindUseObjectContext()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const { handleSetBreakKey } = useSetExtendsKeys()
    const fieldOptions = useMemo(() => getFieldOptions(schema, tableProps), [schema, tableProps])

    const dateFieldOptions = useMemo(() => fieldOptions.filter(item => item.innerType === 'DATE'), [fieldOptions])

    const notRichTextFieldOptions = useMemo(() => fieldOptions.filter(item => item.type !== 'notes'), [fieldOptions])

    return (
        <Group
            label={
                <PopoverQuestion
                    type="calendar-input-describe"
                    renderTarget={(iconEle, ref) => (
                        <>
                            <Flex gap={4} ref={ref} alignItems="center">
                                <Text>依据日期字段生成日期描述</Text>
                                {iconEle}
                            </Flex>
                        </>
                    )}
                />
            }
            mode="switch"
            opened={calendarEnable || false}
            onCollapseChange={v => {
                setValue('calendar.enabled', v)
            }}
        >
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>数据表</Text>
                <Controller
                    control={control}
                    name="calendar.describeDsId"
                    render={({ field }) => (
                        <SelectDataSource
                            placeholder="请选择数据表"
                            size="md"
                            value={field.value}
                            withinPortal
                            onChange={v => {
                                field.onChange(v)
                            }}
                            dataSourceList={dataSourceList}
                        />
                    )}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>匹配的日期字段</Text>
                <Controller
                    control={control}
                    name="calendar.describeFieldId"
                    render={({ field }) => <FieldSelect options={dateFieldOptions} value={field.value} onChange={field.onChange} />}
                />
            </ListItem4ByecodeUi>
            <Controller
                name="calendar.filter"
                control={control}
                render={({ field: { onChange, value } }) => {
                    const filter = value
                    const filtersLength = filter?.expression?.conditions?.length || 0
                    const isHighLight = getIsFindUseInFilter({
                        findUseObject,
                        data: filter,
                        currentDsId: dsId
                    })
                    return (
                        <ListItemMenu
                            width="auto"
                            highlighting={isHighLight}
                            enableHover={false}
                            label="筛选数据"
                            positionDependencies={[filter]}
                            value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                            popupContent={
                                <ViewFieldFilterConfigure
                                    dataSource={dataSource}
                                    dataSourceList={dataSourceList}
                                    pagePathParameter={pagePathParameter}
                                    pageQueryParameter={pageQueryParameter}
                                    pageVariables={pageVariables}
                                    pageStateParameter={pageStateParameter}
                                    availableFieldBlocksInfo={availableFieldBlocksInfo}
                                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                                    columns={columns}
                                    primaryField={primaryField}
                                    filter={filter}
                                    paramsMode={FilterMode.CUSTOM}
                                    noSettingInnerType={notDataSettingInnerTypes}
                                    onFilter={onChange}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                />
                            }
                        />
                    )
                }}
            />
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>展示的描述文本</Text>
                <Controller
                    control={control}
                    name="calendar.describeTextFieldId"
                    render={({ field }) => <FieldSelect options={notRichTextFieldOptions} value={field.value} onChange={field.onChange} />}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="文本颜色" name="breakPoint.calendar.describeFontColor" />
                <Controller
                    control={control}
                    name="breakPoint.calendar.describeFontColor"
                    render={({ field }) => (
                        <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                            <Popover.Target>
                                <FilledInput
                                    value={field.value}
                                    onChange={v => {
                                        field.onChange(v)
                                        handleSetBreakKey('breakPoint.calendar.describeFontColor')
                                    }}
                                    style={{ width: 180 }}
                                />
                            </Popover.Target>
                            <FillPickerPopoverDropdown
                                title="文本颜色"
                                value={field.value}
                                onChange={v => {
                                    field.onChange(v)
                                    handleSetBreakKey('breakPoint.calendar.describeFontColor')
                                }}
                                {...colorSystemMethods}
                            />
                        </Popover>
                    )}
                />
            </ListItem4ByecodeUi>
        </Group>
    )
}
